/**
 * `ez.a.send_email` — *send an {email, email} to {someone, receiver}* (static recipe `174`,
 * app `gmail`, 2 automations on **FIN - MAIN TASK BOARD**).
 *
 * monday publishes no `workflowBlockId` for it: recipe 174 is `static` and has no
 * `overrideStaticRecipeId` in the captured catalogue, so the id is a local `ez.*` one
 * (`specs/M6-A3` §5). Its two slots are monday's own:
 * `email: {subject, body, invalid}` (`configurationFields.email.type === 'email'`) and the
 * seven-option `receiver` picker that includes **Email (column)**.
 *
 * Both production rows put the *recipient address* in `email.body` and the item name in
 * `email.subject` — a real user's data, not a schema fact, so nothing here treats `body`
 * specially. Subject and body are token-interpolated like every other message (§5.7).
 */
import { z } from 'zod';
import { defineAction } from '../block.js';
import { AutomationNotConnected, emailBodySchema } from '../integrations.js';
import { interpolateTokens } from '../tokens.js';
import { receiverSchema } from '../values.js';
import { resolveEmailRecipients } from './email-receiver.js';
import { tokenContextFor } from './token-context.js';
export const sendEmailParams = z.object({
    email: emailBodySchema,
    receiver: receiverSchema,
});
export const sendEmailAction = defineAction({
    id: 'ez.a.send_email',
    kind: 'action',
    app: 'gmail',
    sentencePartial: 'send an {email, email} to {someone, receiver}',
    paramsSchema: sendEmailParams,
    executable: true,
    requiredApp: 'gmail',
    assumed: true,
    references: (params) => [
        { kind: 'app', id: 'gmail' },
        ...(params.receiver.type === 'user' && params.receiver.value !== null && typeof params.receiver.value !== 'object'
            ? [{ kind: 'user', id: String(params.receiver.value) }]
            : []),
    ],
    async execute(context, params) {
        const integrations = context.services.integrations;
        if (integrations === undefined || !(await integrations.isConnected('gmail'))) {
            throw new AutomationNotConnected('gmail');
        }
        const to = await resolveEmailRecipients(context, params.receiver);
        const tokens = await tokenContextFor(context, [params.email.subject, params.email.body]);
        const subject = interpolateTokens(params.email.subject, tokens);
        const body = interpolateTokens(params.email.body, tokens);
        // An empty recipient set is a success with nothing sent — the same rule as `5200` (§12).
        if (to.length === 0)
            return { to: [], subject, sent: false, reason: 'no_recipients' };
        const result = await integrations.sendEmail({ to, subject, body });
        if (!result.ok)
            throw new Error(`gmail.send failed (${result.status}): ${result.detail ?? 'no detail'}`);
        return { to, subject, sent: true, status: result.status, attempts: result.attempts, messageId: result.externalId };
    },
    retryable: (error) => /\((429|5\d\d)\)/.test(error instanceof Error ? error.message : ''),
});
//# sourceMappingURL=ez.a.send-email.js.map